import { Menu as MenuPrimitive } from '@base-ui/react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { useSession } from '@/features/auth/api/useSession'
import { useRaiseInvoiceDraft } from '@/features/finance/api/useRaiseInvoiceDraft'

/* "New document" is a FORK, not a form.
 *
 * ── What it replaced, and why that shape was wrong ─────────────────────────
 *
 * The button used to open a chain of sheets whose first screen offered the
 * case's recorded COSTS as tickboxes. Ticking any of them silently decided the
 * document's kind — charges can only ride on a debit note — so the ordinary act
 * of billing a client began by being asked about the company's own cost side,
 * and answering it turned the client's invoice into a demand on a counterparty.
 * The two documents are not two flavours of one form: an invoice bills the
 * CLIENT out of the rate agreed with them (AD-10), a debit note re-bills a cost
 * to whoever owes it (AD-26). Asking which one first is one press, and it is the
 * press that makes the expense picker legitimate again — it now sits inside the
 * debit-note branch, where a case's costs really are the source of the lines.
 *
 * ── Why the invoice branch does not open a form at all ─────────────────────
 *
 * It posts an empty header and goes straight to the invoice's own page. Nothing
 * on that first screen was a question only the raise could answer: the server
 * copies the client, the codes and the shipment block from the case (AC-2),
 * resolves the FX rate from the economic date (AD-20) and prefills the charges
 * from the accepted quote revision (AD-15). The currency is the one fact the
 * create genuinely freezes (AD-9), and it is taken from the rate the deal was
 * agreed at rather than asked — a case priced in USD invoiced in EUR is a
 * mistake, not a choice — with the invoice date and the economic date defaulting
 * to today the way every other document here defaults them. What is left is a
 * page where the charges, the ceiling and the booking are in view together.
 *
 * ── Each item is there only for a reader who may raise it ──────────────────
 *
 * The two creates are guarded by two capabilities — a client invoice by
 * `document.invoice.render`, a debit note by `document.debitnote.render` — and
 * a role may hold one without the other. An item the reader may not use is
 * ABSENT rather than offered and refused, and with neither there is no fork at
 * all (the README's Authorization rule). The gate is here rather than at the
 * call sites so the case section and the Money page's Documents tab cannot
 * disagree about it.
 */

export function NewDocumentMenu({
  caseId,
  currency,
  onDebitNote,
  /** The raise in flight, so both entry points on a screen can show one
   *  pending state. Supplied by the caller when it already holds the hook —
   *  the case section drives its schedule buttons from the same one. */
  raiseInvoice,
}: {
  caseId: string
  /** The currency this case's rate was agreed in, when the screen knows it. */
  currency?: string
  onDebitNote: () => void
  raiseInvoice?: ReturnType<typeof useRaiseInvoiceDraft>
}) {
  const { t } = useTranslation()
  const { data: user } = useSession()
  const own = useRaiseInvoiceDraft(caseId, currency)
  const invoice = raiseInvoice ?? own

  const mayInvoice = can(user, CAP.documentInvoiceRender)
  const mayDebitNote = can(user, CAP.documentDebitNoteRender)
  if (!mayInvoice && !mayDebitNote) return null

  return (
    <MenuPrimitive.Root
      /* Not modal, for the reason the columns menu gives: this opens from a
         card header that may itself sit inside a scrollable panel, and a second
         scroll lock leaves the page unscrollable after the menu closes. */
      modal={false}
    >
      <MenuPrimitive.Trigger
        render={<Button variant="secondary" size="sm" iconRight={ChevronDown} />}
        disabled={invoice.isPending}
      >
        {t('cases.documents.raise')}
      </MenuPrimitive.Trigger>

      <MenuPrimitive.Portal>
        {/* z-[60], not the in-page popover layer: a portaled popup is a SIBLING
            of any sheet on the page, so anything left at z-40 disappears behind
            a sheet's own panel and reads as a menu that does not open. */}
        <MenuPrimitive.Positioner sideOffset={4} align="end" className="z-[60]">
          <MenuPrimitive.Popup
            aria-label={t('cases.documents.raise')}
            className={cn(
              'bg-surface border-border-subtle rounded-md border',
              'shadow-[var(--shadow-overlay)] w-[322px] max-w-[calc(100vw-2rem)] p-1.5',
              'origin-(--transform-origin)',
              'transition-[opacity,transform] duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
              'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
            )}
          >
            {/* Each item carries its own sentence, because the two words alone
                do not separate them for anybody who has not raised both before:
                which side of the deal the paper faces, and where its lines come
                from, is exactly what somebody is choosing between here. */}
            {mayInvoice ? (
              <MenuItem
                label={t('finance.documents.new.invoice')}
                hint={t('finance.documents.new.invoiceHint')}
                onClick={() => invoice.raise('')}
              />
            ) : null}
            {mayDebitNote ? (
              <MenuItem
                label={t('finance.documents.new.debitNote')}
                hint={t('finance.documents.new.debitNoteHint')}
                onClick={onDebitNote}
              />
            ) : null}
          </MenuPrimitive.Popup>
        </MenuPrimitive.Positioner>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  )
}

function MenuItem({
  label,
  hint,
  onClick,
}: {
  label: string
  hint: string
  onClick: () => void
}) {
  return (
    <MenuPrimitive.Item
      onClick={onClick}
      className={cn(
        'flex cursor-pointer flex-col gap-0.5 rounded-[var(--radius-sm)] px-2.5 py-2',
        'outline-none select-none',
        'data-highlighted:bg-surface-hover',
      )}
    >
      <span className="text-text-heading text-[13px]">{label}</span>
      <span className="text-text-muted text-[11.5px] leading-snug font-light">
        {hint}
      </span>
    </MenuPrimitive.Item>
  )
}
